import { useLocation, useParams } from 'react-router-dom';
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';

import { useApi } from '../../util/lib';
import Button from '../UI/Buttons/Button';
import HtmlHead from '../UI/Head/HtmlHead';
import css from './ServiceReminderSnooze.module.css';
import SuccessBanner from '../NotificationUnsubs/SuccessBanner';
import { useEventTracking } from '../../hooks/useTrackingCampaign';

const isProd = process.env.NODE_ENV === 'production';

const ReminderMaxSnooze =
  parseInt(process.env.REACT_APP_REMINDER_MAX_SNOOZE, 10) || 2;
const ReminderStatusConverted = 'converted';
const SLIDER_MIN_MONTHS = 0;
const SLIDER_MAX_MONTHS = 6;
const SLIDER_STEP = 0.1;
const DEFAULT_SLIDER_MONTHS = 3;

const LoadReminderErrorFallback =
  'We could not load your reminder. Please try again or contact support.';
const UpdateReminderErrorFallback =
  'We could not update your reminder. Please try again or contact support.';

const sanitizeErrorMessage = (error, fallback) => {
  if (isProd) return fallback;
  return (
    // JSON.stringify(error?.stack) ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
};

const addMonths = (date, months) => {
  const next = new Date(date.getTime());
  const wholeMonths = Math.floor(months);
  const fractionalMonths = months - wholeMonths;
  next.setMonth(next.getMonth() + wholeMonths);
  if (fractionalMonths > 0) {
    next.setDate(next.getDate() + Math.round(fractionalMonths * 30.44));
  }
  return next;
};

const calendarMonthsApart = (yyyyMmDd) => {
  const target = parseDateInput(yyyyMmDd);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (Number.isNaN(target.getTime())) return null;

  let years = target.getFullYear() - today.getFullYear();
  let months = target.getMonth() - today.getMonth();
  if (target.getDate() < today.getDate()) {
    months -= 1;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return {
    years,
    months,
    totalMonths: years * 12 + months,
  };
};

const toDateInputValue = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseDateInput = (yyyyMmDd) => new Date(`${yyyyMmDd}T00:00:00`);

const formatDisplayDate = (value) => {
  if (!value) return '';
  const parsed = parseDateInput(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatShortDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return '';
  const parsed = parseDateInput(yyyyMmDd);
  if (Number.isNaN(parsed.getTime())) return yyyyMmDd;
  return parsed.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatSuccessDate = (value) => {
  if (!value) return '';
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const dateToSliderMonths = (yyyyMmDd) => {
  const today = startOfToday();
  const target = parseDateInput(yyyyMmDd);
  if (Number.isNaN(target.getTime())) return DEFAULT_SLIDER_MONTHS;
  if (target.getTime() <= today.getTime()) return SLIDER_MIN_MONTHS;

  const maxDate = addMonths(today, SLIDER_MAX_MONTHS);
  const minTime = today.getTime();
  const maxTime = maxDate.getTime();
  const targetTime = target.getTime();

  if (targetTime >= maxTime) return SLIDER_MAX_MONTHS;

  const ratio = (targetTime - minTime) / (maxTime - minTime);
  const months = SLIDER_MIN_MONTHS + ratio * (SLIDER_MAX_MONTHS - SLIDER_MIN_MONTHS);
  return Math.round(months * 10) / 10;
};

const formatRelativeDuration = (yyyyMmDd) => {
  const calendar = calendarMonthsApart(yyyyMmDd);
  if (!calendar) return null;

  if (calendar.totalMonths < 12) {
    if (calendar.totalMonths <= 0) {
      return 'less than 1 month';
    }
    if (calendar.totalMonths === 1) {
      return '1 month';
    }
    return `${calendar.totalMonths} months`;
  }

  const { years, months } = calendar;
  const yearLabel = years === 1 ? '1 year' : `${years} years`;

  if (months === 0) {
    return yearLabel;
  }

  const monthLabel = months === 1 ? '1 month' : `${months} months`;
  return `${yearLabel} ${monthLabel}`;
};

const remindAtFromSliderMonths = (months) => {
  const today = startOfToday();
  if (months <= SLIDER_MIN_MONTHS) {
    return toDateInputValue(today);
  }
  return toDateInputValue(addMonths(today, months));
};

const monthsToSliderValue = (months) => {
  if (months == null) return DEFAULT_SLIDER_MONTHS;
  return Math.min(
    SLIDER_MAX_MONTHS,
    Math.max(SLIDER_MIN_MONTHS, months),
  );
};

const sliderProgress = (months) => {
  const range = SLIDER_MAX_MONTHS - SLIDER_MIN_MONTHS;
  if (range <= 0) return '0%';
  const pct = ((months - SLIDER_MIN_MONTHS) / range) * 100;
  return `${Math.min(100, Math.max(0, pct))}%`;
};

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const minRemindAt = () => toDateInputValue(startOfToday());

const maxRemindAt = () => {
  return toDateInputValue(addMonths(startOfToday(), SLIDER_MAX_MONTHS));
};

const clampRemindAt = (dateValue) => {
  const min = minRemindAt();
  const max = maxRemindAt();
  if (dateValue < min) return min;
  if (dateValue > max) return max;
  return dateValue;
};

const toRemindAtPayload = (value) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }
  return new Date(value);
};

const ClockIcon = () => (
  <svg
    className={css.TitleIcon}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75" />
    <path
      d="M12 7v5l3 2"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg
    className={css.CalendarIcon}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect
      x="3"
      y="5"
      width="18"
      height="16"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path d="M3 10h18" stroke="currentColor" strokeWidth="1.75" />
    <path
      d="M8 3v4M16 3v4"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

const ConvertedIcon = () => (
  <svg
    className={css.TitleIcon}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75" />
    <path
      d="M8 12.5l2.5 2.5 5.5-5.5"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ServiceReminderSnooze = () => {
  const api = useApi();
  const location = useLocation();
  const { track } = useEventTracking();
  const { code: routeId } = useParams();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [remindAt, setRemindAt] = useState('');
  const [snoozeCount, setSnoozeCount] = useState(0);
  const [snoozedUntil, setSnoozedUntil] = useState();
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [reminderStatus, setReminderStatus] = useState(null);
  const [serviceReminder, setServiceReminder] = useState(null);
  const [sliderMonths, setSliderMonths] = useState(DEFAULT_SLIDER_MONTHS);
  
  const serviceReminderId = serviceReminder?._id;
  const minDate = useMemo(() => minRemindAt(), []);
  const maxDate = useMemo(() => maxRemindAt(), []);
  const hasReachedMaxSnooze = snoozeCount >= ReminderMaxSnooze;
  const remainingSnoozes = Math.max(0, ReminderMaxSnooze - snoozeCount);
  const isReminderConverted = reminderStatus === ReminderStatusConverted;

  const relativeDuration = useMemo(
    () => formatRelativeDuration(remindAt),
    [remindAt],
  );

  const snooze = useCallback(async (remindAtValue) => {
    if (!remindAtValue || !serviceReminderId) return false;
    if (snoozeCount >= ReminderMaxSnooze) {
      setError(
        `You have reached the maximum of ${ReminderMaxSnooze} snoozes for this reminder.`,
      );
      return false;
    }
    setSubmitting(true);
    setError(null);
    if (showSuccess) setShowSuccess(false);

    try {
      const response = await api({
        method: 'post',
        responseType: 'json',
        url: `/reminders/${serviceReminderId}/snooze`,
        data: { remindAt: toRemindAtPayload(remindAtValue) },
      });
      const reminder = response.data?.reminder;
      const until = reminder?.snoozedUntil;
      if (!until) {
        throw new Error('Snooze failed');
      }
      setSnoozedUntil(until);
      if (typeof reminder.snoozeCount === 'number') {
        setSnoozeCount(reminder.snoozeCount);
      } else {
        setSnoozeCount((count) => count + 1);
      }
      if (reminder.status) {
        setReminderStatus(reminder.status);
      }
      setShowSuccess(true);
      return true;
    } catch (err) {
      setError(sanitizeErrorMessage(err, UpdateReminderErrorFallback));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [api, serviceReminderId, showSuccess, snoozeCount]);

  const applySliderMonths = useCallback((months) => {
    const value = monthsToSliderValue(months);
    setSliderMonths(value);
    setRemindAt(remindAtFromSliderMonths(value));
  }, []);

  const applyRemindAt = useCallback((dateValue) => {
    const clamped = clampRemindAt(dateValue);
    setRemindAt(clamped);
    setSliderMonths(dateToSliderMonths(clamped));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await api({
          method: 'get',
          responseType: 'json',
          url: `/reminders/${routeId}`,
        });
        if (cancelled) return;

        const { reminder } = response.data || {};
        if (!reminder?._id) {
          setError('This reminder link is invalid or has expired.');
          return;
        }

        setServiceReminder(reminder);
        setSnoozeCount(
          typeof reminder.snoozeCount === 'number' ? reminder.snoozeCount : 0,
        );
        setReminderStatus(reminder.status || null);

        if (reminder.status === ReminderStatusConverted) {
          return;
        }

        if (reminder.snoozedUntil && new Date(reminder.snoozedUntil) > new Date()) {
          const existingDate = toDateInputValue(new Date(reminder.snoozedUntil));
          applyRemindAt(existingDate);
          setSnoozedUntil(reminder.snoozedUntil);
        } else {
          applySliderMonths(DEFAULT_SLIDER_MONTHS);
        }
      } catch (err) {
        if (!cancelled) {
          setError(sanitizeErrorMessage(err, LoadReminderErrorFallback));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [api, routeId, applyRemindAt, applySliderMonths]);

  useEffect(() => {
    if (!serviceReminder?._id) return;

    track('pageView', {
      serviceReminderCode: routeId,
      contextId: serviceReminder._id,
      feature: 'service-reminder-snooze',
      workshopId: serviceReminder.workshopId,
      serviceReminderId: serviceReminder._id,
      pagePath: `${location.pathname}${location.search}`,
    });
  }, [
    track,
    routeId,
    serviceReminder,
    location.search,
    location.pathname,
  ]);

  const onSliderChange = (e) => {
    applySliderMonths(parseFloat(e.target.value));
  };

  const onDateChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    applyRemindAt(value);
  };

  const submitLabel = useMemo(() => {
    if (submitting) return 'Saving...';
    if (remindAt) {
      return `Remind me at ${formatShortDate(remindAt)}`;
    }
    return 'Remind me';
  }, [submitting, remindAt]);

  const canSubmit = Boolean(
    remindAt && serviceReminderId && !hasReachedMaxSnooze && !isReminderConverted,
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !canSubmit) return;
    await snooze(remindAt);
  };

  const snoozeUsageLabel = useMemo(() => {
    if (hasReachedMaxSnooze) {
      return `You've used all ${ReminderMaxSnooze} snoozes for this reminder.`;
    }
    if (snoozeCount === 0) {
      return `You can snooze this reminder up to ${ReminderMaxSnooze} times.`;
    }
    if (remainingSnoozes === 1) {
      return `You've snoozed ${snoozeCount} of ${ReminderMaxSnooze} times · 1 snooze left.`;
    }
    return `You've snoozed ${snoozeCount} of ${ReminderMaxSnooze} times · ${remainingSnoozes} snoozes left.`;
  }, [hasReachedMaxSnooze, remainingSnoozes, snoozeCount]);

  const successMessage = useMemo(() => {
    if (!snoozedUntil) {
      return 'Your service reminder has been updated.';
    }
    const dateLabel = formatSuccessDate(snoozedUntil);
    if (hasReachedMaxSnooze) {
      return `We'll remind you about your service on ${dateLabel}. You've used all ${ReminderMaxSnooze} snoozes for this reminder.`;
    }
    return `We'll remind you about your service on ${dateLabel}. You've snoozed ${snoozeCount} of ${ReminderMaxSnooze} times.`;
  }, [hasReachedMaxSnooze, snoozeCount, snoozedUntil]);

  if (loading && !showSuccess && !error) {
    return (
      <>
        <HtmlHead repairy />
        <div className={css.Loading}>
          <img alt="" src="/rpry-icon.svg" className={css.Logo} />
        </div>
      </>
    );
  }

  return (
    <>
      <HtmlHead repairy />
      <div className={css.Page}>
        <div className={css.Card}>
          <img
            src="/rpry-icon.svg"
            alt="Repairy"
            className={css.BrandLogo}
          />

          <SuccessBanner
            show={showSuccess}
            onDismiss={() => setShowSuccess(false)}
            title="Your service reminder was saved"
            message={successMessage}
          />

          {error && (
            <p className={css.Error} role="alert">
              {!isProd && (
                <>
                  <span>
                    You&apos;re viewing this message because you're in development mode.
                  </span>
                  <br/>
                  <br/>
                </>
              )}
              {error}
            </p>
          )}

          {!error && isReminderConverted && (
            <div className={css.ConvertedState}>
              <div className={css.TitleRow}>
                <ConvertedIcon />
                <h1 className={css.Title}>Already booked</h1>
              </div>
              <p className={css.Subtitle}>
                This service reminder has already been converted into a
                booking, so it can&apos;t be snoozed from this link.
              </p>
            </div>
          )}

          {!error && serviceReminderId && !isReminderConverted && (
            <form onSubmit={onSubmit}>
              <div className={css.TitleRow}>
                <ClockIcon />
                <h1 className={css.Title}>Remind me later</h1>
              </div>
              <p className={css.Subtitle}>
                Choose when we should remind you about your next service.
              </p>
              <p className={css.SnoozeUsage} aria-live="polite">
                {snoozeUsageLabel}
              </p>

              {hasReachedMaxSnooze ? (
                <div className={css.DatePreview}>
                  <p className={css.SelectedDate}>
                    {formatDisplayDate(
                      snoozedUntil
                        ? toDateInputValue(new Date(snoozedUntil))
                        : remindAt,
                    )}
                  </p>
                  <p className={css.RelativeMonths}>
                    Your reminder date can no longer be changed.
                  </p>
                </div>
              ) : (
                <>
                  <div className={css.DatePreview}>
                    <p className={css.SelectedDate}>
                      {formatDisplayDate(remindAt)}
                    </p>
                    {relativeDuration && (
                      <p className={css.RelativeMonths}>
                        ( That&apos;s in {relativeDuration} )
                      </p>
                    )}
                  </div>

                  <div className={css.SliderSection}>
                    <input
                      type="range"
                      className={css.Slider}
                      min={SLIDER_MIN_MONTHS}
                      max={SLIDER_MAX_MONTHS}
                      step={SLIDER_STEP}
                      value={sliderMonths}
                      onChange={onSliderChange}
                      style={{ '--slider-progress': sliderProgress(sliderMonths) }}
                      aria-label="Choose reminder duration in months"
                    />
                    <div className={css.SliderLabels}>
                      <span>Today</span>
                      <span>3 months</span>
                      <span>6 months</span>
                    </div>
                  </div>

                  <div className={css.Divider}>or</div>

                  <div className={css.DateInputSection}>
                    <label className={css.DateInputLabel} htmlFor="remindAt">
                      Or choose a specific date
                    </label>
                    <div className={css.DateInputWrap}>
                      <input
                        id="remindAt"
                        type="date"
                        className={css.DateField}
                        min={minDate}
                        max={maxDate}
                        value={remindAt}
                        onChange={onDateChange}
                        required
                      />
                      <CalendarIcon />
                    </div>
                    <p className={css.DateHint}>
                      You can type a date or use the calendar icon.
                    </p>
                  </div>

                  <Button
                    type="main"
                    onClick={onSubmit}
                    disabled={submitting || !canSubmit}
                    css={[
                      css.Submit,
                      submitting && css.SubmitDisabled,
                    ].join(' ')}
                  >
                    {submitLabel}
                  </Button>
                </>
              )}
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ServiceReminderSnooze;
